<template>
  <v-menu v-model="menu" left offset-y :close-on-content-click="true">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        :disabled="disabled || exporting"
        :loading="exporting"
        class="stat-export-trigger"
        :aria-label="title + ' exportieren'"
        v-bind="attrs"
        v-on="on">
        <v-icon color="grey lighten-1">mdi-download</v-icon>
      </v-btn>
    </template>

    <v-list dark class="stat-export-menu py-2">
      <v-subheader class="stat-export-menu__title">Statistik herunterladen</v-subheader>
      <v-list-item v-for="option in exportOptions" :key="option.type" class="stat-export-menu__item" @click="runExport(option.type)">
        <v-list-item-icon class="stat-export-menu__icon">
          <v-icon :color="option.color">{{ option.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ option.title }}</v-list-item-title>
          <v-list-item-subtitle>{{ option.subtitle }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import ApexCharts from "apexcharts"
import { saveAs } from "file-saver"
import writeXlsxFile from "write-excel-file"

export default {
  name: "StatisticExportMenu",
  props: {
    title: {
      type: String,
      required: true,
    },
    fileKey: {
      type: String,
      default: "statistik",
    },
    date: {
      type: String,
      default: "",
    },
    categories: {
      type: Array,
      default: () => [],
    },
    series: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Array,
      default: () => [],
    },
    accent: {
      type: String,
      default: "#58d388",
    },
    chartMode: {
      type: String,
      default: "auto",
      validator: (value) => ["auto", "grouped", "stacked"].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menu: false,
      exporting: false,
      exportOptions: [
        {
          type: "xlsx",
          title: "Excel-Datei",
          subtitle: "Formatiert und vollständig",
          icon: "mdi-microsoft-excel",
          color: "#58d388",
        },
        {
          type: "csv",
          title: "CSV-Datei",
          subtitle: "Für Excel und weitere Tools",
          icon: "mdi-file-delimited-outline",
          color: "#58b6ff",
        },
        {
          type: "png",
          title: "Bild herunterladen",
          subtitle: "Gestaltete PNG-Grafik mit Zahlen",
          icon: "mdi-image-outline",
          color: "#f5b942",
        },
      ],
    }
  },
  methods: {
    async runExport(type) {
      if (this.exporting) return

      this.exporting = true
      try {
        if (type === "xlsx") await this.exportXlsx()
        if (type === "csv") await this.exportCsv()
        if (type === "png") await this.exportPng()

        if (this.$toast) this.$toast.success("Export wurde erstellt")
      } catch (error) {
        console.error("Statistik-Export fehlgeschlagen:", error)
        if (this.$toast) this.$toast.error("Export konnte nicht erstellt werden")
      } finally {
        this.exporting = false
      }
    },

    normalizeData() {
      const labels = []
      const addLabel = (label) => {
        const value = label === undefined || label === null || label === "" ? "Ohne Bezeichnung" : String(label)
        if (!labels.includes(value)) labels.push(value)
        return value
      }

      ;(this.categories || []).forEach(addLabel)

      const normalizedSeries = (this.series || []).map((item, seriesIndex) => {
        const values = new Map()
        const points = item && Array.isArray(item.data) ? item.data : []

        points.forEach((point, pointIndex) => {
          const isPoint = point !== null && typeof point === "object" && !Array.isArray(point)
          const rawLabel = isPoint ? point.x ?? point.name ?? this.categories[pointIndex] : this.categories[pointIndex]
          const label = addLabel(rawLabel !== undefined ? rawLabel : pointIndex + 1)
          const rawValue = isPoint ? point.y ?? point.value : point
          values.set(label, this.normalizeValue(rawValue))
        })

        return {
          name: item && item.name ? String(item.name) : `Wert ${seriesIndex + 1}`,
          values,
        }
      })

      return {
        headers: ["Kategorie", ...normalizedSeries.map((item) => item.name)],
        rows: labels.map((label) => [label, ...normalizedSeries.map((item) => (item.values.has(label) ? item.values.get(label) : 0))]),
      }
    },

    normalizeValue(value) {
      if (value === null || value === undefined || value === "") return 0
      if (typeof value === "number") return Number.isFinite(value) ? value : 0

      const number = Number(value)
      return Number.isFinite(number) && String(value).trim() !== "" ? number : String(value)
    },

    async exportXlsx() {
      const table = this.normalizeData()
      const columnCount = Math.max(table.headers.length, 2)
      const titleRow = Array.from({ length: columnCount }, (_, index) => ({
        value: index === 0 ? this.title : "",
        type: String,
        fontWeight: "bold",
        fontSize: index === 0 ? 18 : 12,
        color: "#ffffff",
        backgroundColor: "#173526",
        height: 34,
      }))
      const rows = [
        titleRow,
        [
          { value: "Datenstand", type: String, fontWeight: "bold", color: "#173526" },
          { value: this.formatDate(this.date), type: String },
        ],
        [
          { value: "Exportiert am", type: String, fontWeight: "bold", color: "#173526" },
          { value: new Date().toLocaleString("de-CH"), type: String },
        ],
      ]

      if (this.summary.length) {
        rows.push([])
        this.summary.forEach((item) => {
          rows.push([
            { value: item.label || "Kennzahl", type: String, fontWeight: "bold", color: "#173526" },
            this.getExcelCell(item.value),
          ])
        })
      }

      rows.push([])
      rows.push(
        table.headers.map((header) => ({
          value: header,
          type: String,
          fontWeight: "bold",
          color: "#102018",
          backgroundColor: "#58d388",
          align: header === "Kategorie" ? "left" : "right",
          height: 28,
        })),
      )

      table.rows.forEach((row, rowIndex) => {
        rows.push(
          row.map((value, cellIndex) => ({
            ...this.getExcelCell(value),
            backgroundColor: rowIndex % 2 === 0 ? "#f1f7f3" : "#ffffff",
            align: cellIndex === 0 ? "left" : "right",
          })),
        )
      })

      const columns = table.headers.map((header, index) => ({ width: index === 0 ? Math.max(28, Math.min(46, header.length + 18)) : 22 }))
      const blob = await writeXlsxFile(rows, { columns })
      await this.downloadBlob(blob, "xlsx")
    },

    getExcelCell(value) {
      if (typeof value === "number" && Number.isFinite(value)) return { value, type: Number }
      return { value: value === null || value === undefined ? "" : String(value), type: String }
    },

    async exportCsv() {
      const table = this.normalizeData()
      const rows = [
        [this.title],
        ["Datenstand", this.formatDate(this.date)],
        ["Exportiert am", new Date().toLocaleString("de-CH")],
      ]

      if (this.summary.length) {
        rows.push([])
        this.summary.forEach((item) => rows.push([item.label || "Kennzahl", item.value]))
      }

      rows.push([])
      rows.push(table.headers)
      rows.push(...table.rows)

      const csv = "\ufeff" + rows.map((row) => row.map(this.csvCell).join(";")).join("\r\n")
      await this.downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), "csv")
    },

    csvCell(value) {
      if (typeof value === "number" && Number.isFinite(value)) return `"${value}"`

      let text = value === null || value === undefined ? "" : String(value)
      if (/^[=+\-@]/.test(text.trim())) text = `'${text}`
      return `"${text.replace(/"/g, '""')}"`
    },

    async exportPng() {
      const table = this.normalizeData()
      const chartData = this.getImageChartData(table)
      const width = this.getImageChartWidth(chartData.categories.length)
      const height = this.getImageChartHeight(chartData.categories.length, chartData.series.length)
      const target = document.createElement("div")
      let chart = null

      target.setAttribute("aria-hidden", "true")
      target.style.position = "fixed"
      target.style.left = "-100000px"
      target.style.top = "0"
      target.style.width = `${width}px`
      target.style.height = `${height}px`
      target.style.pointerEvents = "none"
      target.style.background = "#0b130e"
      document.body.appendChild(target)

      try {
        chart = new ApexCharts(target, this.getImageChartOptions(chartData, width, height))
        await chart.render()
        this.improveChartDataLabels(target, chartData)

        const result = await chart.dataURI({ scale: 1 })
        const blob = result && typeof Blob !== "undefined" && result.blob instanceof Blob ? result.blob : await this.dataUriToBlob(result && result.imgURI)
        if (!blob) throw new Error("PNG konnte nicht erzeugt werden")

        await this.downloadBlob(blob, "png")
      } finally {
        if (chart) chart.destroy()
        if (target.parentNode) target.parentNode.removeChild(target)
      }
    },

    getImageChartData(table) {
      const maxCategories = 60
      const sourceRows = table.rows.length ? table.rows : [["Keine Daten", 0]]
      const rows = sourceRows.slice(0, maxCategories)
      const seriesHeaders = table.headers.length > 1 ? table.headers.slice(1) : ["Wert"]

      return {
        categories: rows.map((row) => String(row[0])),
        series: seriesHeaders.map((name, index) => ({
          name,
          group: this.series[index] && this.series[index].group ? String(this.series[index].group) : undefined,
          data: rows.map((row) => this.toChartNumber(row[index + 1])),
        })),
        totalCategories: sourceRows.length,
        limited: sourceRows.length > maxCategories,
      }
    },

    getImageChartHeight(categoryCount, seriesCount) {
      if (categoryCount > 20 || seriesCount > 8) return 1100
      if (categoryCount > 12 || seriesCount > 4) return 1000
      return 900
    },

    getImageChartWidth(categoryCount) {
      if (categoryCount <= 18) return 1600
      return Math.min(5200, 520 + categoryCount * 78)
    },

    getImageChartOptions(chartData, width, height) {
      const stacked = this.isImageChartStacked(chartData)
      const grouped = !stacked && chartData.series.length > 1
      const distributed = !stacked && chartData.categories.length <= 8
      const hasNegativeValues = chartData.series.some((series) => series.data.some((value) => value < 0))
      const colors = this.getChartColors(Math.max(chartData.series.length, chartData.categories.length))
      const dataLabelFontSize = this.getDataLabelFontSize(chartData)
      const summaryText = this.summary
        .slice(0, 4)
        .map((item) => `${item.label || "Kennzahl"}: ${this.formatValue(item.value)}`)
        .join("  ·  ")
      const limitText = chartData.limited ? `  ·  Bild zeigt ${chartData.categories.length} von ${chartData.totalCategories} Kategorien` : ""
      const subtitle = `Datenstand ${this.formatDate(this.date)}${summaryText ? `  ·  ${summaryText}` : ""}${limitText}`
      const formatValue = (value) => this.formatValue(value)

      return {
        chart: {
          type: "bar",
          width,
          height,
          background: "#0b130e",
          foreColor: "#ffffff",
          stacked,
          animations: { enabled: false },
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: '"Saira", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        series: chartData.series,
        colors,
        theme: { mode: "dark" },
        title: {
          text: this.title,
          align: "left",
          margin: 28,
          offsetX: 28,
          offsetY: 24,
          style: {
            color: "#ffffff",
            fontSize: "32px",
            fontWeight: 800,
          },
        },
        subtitle: {
          text: subtitle,
          align: "left",
          offsetX: 28,
          offsetY: 72,
          style: {
            color: "#aab4ae",
            fontSize: "16px",
            fontWeight: 500,
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: grouped ? "70%" : chartData.categories.length > 18 ? "72%" : "58%",
            borderRadius: 7,
            distributed,
            dataLabels: {
              position: stacked ? "center" : "top",
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter(value) {
            return Number(value) === 0 ? "" : formatValue(value)
          },
          offsetY: stacked ? 0 : grouped ? -10 : -14,
          style: {
            fontSize: dataLabelFontSize,
            fontWeight: 800,
            colors: ["#ffffff"],
          },
          background: {
            enabled: false,
          },
          dropShadow: {
            enabled: true,
            left: 0,
            top: 2,
            blur: 3,
            color: "#000000",
            opacity: 0.4,
          },
        },
        stroke: {
          show: true,
          width: 1,
          colors: ["rgba(255, 255, 255, 0.16)"],
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "dark",
            type: "vertical",
            shadeIntensity: 0.18,
            opacityFrom: 1,
            opacityTo: 0.82,
            stops: [0, 100],
          },
        },
        grid: {
          borderColor: "rgba(255, 255, 255, 0.1)",
          strokeDashArray: 4,
          padding: {
            top: 92,
            right: 34,
            bottom: 18,
            left: 24,
          },
        },
        xaxis: {
          categories: chartData.categories,
          labels: {
            rotate: chartData.categories.length > 10 ? -45 : 0,
            rotateAlways: chartData.categories.length > 10,
            trim: false,
            hideOverlappingLabels: false,
            style: {
              colors: chartData.categories.map(() => "#d5ddd8"),
              fontSize: "15px",
              fontWeight: 600,
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: hasNegativeValues ? undefined : 0,
          forceNiceScale: true,
          labels: {
            formatter(value) {
              return formatValue(value)
            },
            style: {
              colors: ["#aab4ae"],
              fontSize: "16px",
              fontWeight: 600,
            },
          },
        },
        legend: {
          show: chartData.series.length > 1,
          position: "top",
          horizontalAlign: "right",
          offsetX: -18,
          offsetY: 20,
          fontSize: "14px",
          fontWeight: 600,
          labels: { colors: "#ffffff" },
          markers: { width: 12, height: 12, radius: 4 },
          itemMargin: { horizontal: 12, vertical: 6 },
        },
        tooltip: { enabled: false },
        noData: {
          text: "Keine Daten verfügbar",
          align: "center",
          verticalAlign: "middle",
          style: {
            color: "#aab4ae",
            fontSize: "16px",
          },
        },
      }
    },

    getChartColors(count) {
      const palette = [this.accent, "#58b6ff", "#f5b942", "#b788ff", "#ff718d", "#52d8d8", "#ff934f", "#8bd450", "#d96fff", "#a6b2ab"]
      return Array.from({ length: Math.max(1, count) }, (_, index) => palette[index % palette.length])
    },

    getDataLabelFontSize(chartData) {
      const grouped = !this.isImageChartStacked(chartData) && chartData.series.length > 1

      if (chartData.categories.length > 24 || chartData.series.length > 8) return "14px"
      if (grouped || chartData.categories.length > 12 || chartData.series.length > 4) return "15px"
      return "17px"
    },

    improveChartDataLabels(target, chartData) {
      const fontSize = this.getDataLabelFontSize(chartData)
      const grouped = !this.isImageChartStacked(chartData) && chartData.series.length > 1
      const groups = Array.from(target.querySelectorAll("g.apexcharts-datalabels")).filter(
        (group) => !group.parentElement || !group.parentElement.closest("g.apexcharts-datalabels"),
      )

      if (groups.length) {
        groups.forEach((group, groupIndex) => {
          const seriesAttribute = group.getAttribute("data:realIndex") || group.getAttribute("data-real-index")
          const rawSeriesIndex = seriesAttribute === null ? Number.NaN : Number(seriesAttribute)
          const seriesIndex = Number.isFinite(rawSeriesIndex) ? rawSeriesIndex : groupIndex % chartData.series.length

          group.querySelectorAll("text.apexcharts-datalabel, text.apexcharts-data-label").forEach((label, labelIndex) => {
            const pointIndex = this.getChartDataPointIndex(label, labelIndex)
            const yOffset = grouped ? this.getGroupedLabelOffset(seriesIndex, pointIndex, chartData) : 0
            this.styleChartDataLabel(label, fontSize)
            this.shiftSvgElementY(label, yOffset)
          })
        })
        return
      }

      target.querySelectorAll("text.apexcharts-datalabel, text.apexcharts-data-label").forEach((label, labelIndex) => {
        const seriesIndex = Math.min(chartData.series.length - 1, Math.floor(labelIndex / Math.max(1, chartData.categories.length)))
        const pointIndex = labelIndex % Math.max(1, chartData.categories.length)
        const yOffset = grouped ? this.getGroupedLabelOffset(seriesIndex, pointIndex, chartData) : 0
        this.styleChartDataLabel(label, fontSize)
        this.shiftSvgElementY(label, yOffset)
      })
    },

    isImageChartStacked(chartData) {
      return this.chartMode === "stacked" || (this.chartMode === "auto" && chartData.series.length > 1)
    },

    getGroupedLabelOffset(seriesIndex, pointIndex, chartData) {
      const rankedSeries = chartData.series
        .map((series, index) => ({ index, value: Number(series.data[pointIndex]) || 0 }))
        .sort((a, b) => b.value - a.value || a.index - b.index)
      const rank = rankedSeries.findIndex((series) => series.index === seriesIndex)
      const spacing = chartData.series.length > 2 ? 24 : 30

      return Math.round((rank - (chartData.series.length - 1) / 2) * spacing)
    },

    getChartDataPointIndex(element, fallbackIndex) {
      const attributes = ["data:pointIndex", "data-point-index", "dataPointIndex", "j"]

      for (const attribute of attributes) {
        const value = element.getAttribute(attribute)
        if (value !== null && Number.isFinite(Number(value))) return Number(value)
      }

      return fallbackIndex
    },

    styleChartDataLabel(label, fontSize) {
      label.setAttribute("fill", "#ffffff")
      label.setAttribute("stroke", "#06100a")
      label.setAttribute("stroke-width", "1.5")
      label.setAttribute("stroke-linejoin", "round")
      label.setAttribute("paint-order", "stroke fill")
      label.setAttribute("font-size", fontSize)
      label.setAttribute("font-weight", "800")
      label.style.fill = "#ffffff"
      label.style.stroke = "#06100a"
      label.style.strokeWidth = "1.5px"
      label.style.paintOrder = "stroke fill"
    },

    shiftSvgElementY(element, offset) {
      if (!offset) return

      const y = Number(element.getAttribute("y"))
      if (Number.isFinite(y)) {
        element.setAttribute("y", String(y + offset))
      }
    },

    toChartNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },

    async dataUriToBlob(uri) {
      if (!uri) throw new Error("PNG-Daten fehlen")
      const response = await fetch(uri)
      return response.blob()
    },

    formatValue(value) {
      if (typeof value === "number" && Number.isFinite(value)) {
        return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 2 }).format(value)
      }
      return value === null || value === undefined || value === "" ? "0" : String(value)
    },

    formatDate(value) {
      if (!value) return "Aktuell"
      const parts = String(value).split("-")
      if (parts.length === 2) return `${parts[1]}.${parts[0]}`
      if (parts.length !== 3) return String(value)
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },

    async downloadBlob(blob, extension) {
      const fileName = `${this.sanitizeFileName(this.fileKey || this.title)}_${this.date || new Date().toISOString().substr(0, 10)}`

      if (typeof window !== "undefined" && window.flutter_inappwebview) {
        await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => {
            try {
              const base64 = String(reader.result).split(",")[1]
              Promise.resolve(window.flutter_inappwebview.callHandler("blobToBase64Handler", base64, extension, fileName)).then(resolve).catch(reject)
            } catch (error) {
              reject(error)
            }
          }
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        return
      }

      saveAs(blob, `${fileName}.${extension}`)
    },

    sanitizeFileName(value) {
      return String(value || "statistik")
        .trim()
        .toLowerCase()
        .replace(/[ä]/g, "ae")
        .replace(/[ö]/g, "oe")
        .replace(/[ü]/g, "ue")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    },
  },
}
</script>

<style scoped>
.stat-export-trigger {
  width: 2.5rem;
  height: 2.5rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.035);
}

.stat-export-trigger:hover {
  border-color: rgba(88, 211, 136, 0.45);
  background: rgba(88, 211, 136, 0.1);
}

.stat-export-menu {
  width: 18rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  background: #141a16 !important;
  box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.34);
}

.stat-export-menu__title {
  min-height: 2.25rem;
  color: #c5cec8 !important;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.stat-export-menu__item {
  min-height: 4rem;
  margin: 0.25rem 0.5rem;
  border-radius: 0.75rem;
}

.stat-export-menu__item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.stat-export-menu__icon {
  min-width: 2.5rem;
  margin-right: 0.5rem !important;
}

.stat-export-menu__item .v-list-item__title {
  color: #ffffff;
  font-size: 0.9375rem;
  font-weight: 700;
}

.stat-export-menu__item .v-list-item__subtitle {
  margin-top: 0.1875rem;
  color: #aab4ae !important;
  font-size: 0.8125rem;
  line-height: 1.25rem;
}
</style>
